import React, { useEffect, useRef } from "react";
import { ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import AnalysisReportTab from "./AnalysisReportTab";

export function SessionAnalysisReportButton({
  open,
  onToggle,
  reportId,
}: {
  open: boolean;
  onToggle: () => void;
  reportId: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={open ? reportId : undefined}
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface px-2.5 py-1 text-xs font-medium text-text-dim shadow-sm hover:bg-muted-surface hover:text-text focus-visible:outline-info"
    >
      <ChevronRight
        className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
        aria-hidden="true"
      />
      {t("analysisReport.analysis_report")}
      <span className="rounded-full border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-strong">
        Admin
      </span>
    </button>
  );
}

/** Admin diagnostics stay unmounted until explicitly opened from the header. */
export default function SessionAnalysisReport({
  open,
  reportId,
  onClose,
  expandFromLink = false,
  ...reportProps
}: React.ComponentProps<typeof AnalysisReportTab> & {
  open: boolean;
  reportId: string;
  expandFromLink?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!expandFromLink || !open) return;
    const frame = requestAnimationFrame(() => {
      section.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [expandFromLink, open]);

  if (!open) return null;
  return (
    <section
      id={reportId}
      ref={section}
      aria-label={t("analysisReport.analysis_report")}
      className="scroll-mt-5 rounded-xl border border-outline bg-surface p-5 @[1100px]:p-6"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-text">{t("analysisReport.analysis_report")}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("close")}
          title={t("close")}
          className="rounded-md p-1.5 text-text-dim hover:bg-muted-surface hover:text-text focus-visible:outline-info"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <AnalysisReportTab {...reportProps} />
    </section>
  );
}
